import React from 'react'
import { getCountryName, getStateName } from '../../../constants/shippingZones'
import type { MailOrderShippingZone } from '../../../../../types/location'
import type { CategoryLookups } from '../../../utils/categoryLookups'
import { formatTaxPercent, getEffectiveZoneTax } from '../../../utils/shippingTax'
import { CategoryAvatar, CopyIcon } from '../../common'
import { Tooltip } from '../tabs/payments/shared/Tooltip'

// Shared grid so the Shipping Zones card header lines up with every row.
// Columns: Zone Name | Region | Shipping Fee | Min. Purchase | Free Shipping At
// | Shipping Tax | Exclusions | actions (expand / edit / duplicate / delete).
export const zoneTableRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '2fr 1.2fr 1fr 1fr 1.2fr 1.1fr 0.9fr 144px',
  gap: 8,
  alignItems: 'center',
  padding: '12px 20px',
  fontSize: 13,
  color: '#2C3E50',
  borderBottom: '1px solid #F1F5F9',
}

export const zoneTableHeaderRowStyle: React.CSSProperties = {
  ...zoneTableRowStyle,
  padding: '10px 20px',
  fontSize: 11,
  fontWeight: 600,
  color: '#94A3B8',
  textTransform: 'uppercase',
  letterSpacing: '.06em',
  background: '#FAFBFC',
}

const iconBtnStyle: React.CSSProperties = {
  padding: 6,
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: '#7F8C8D',
  borderRadius: 6,
  display: 'inline-flex',
}

const warningBadgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 10px',
  fontSize: 11,
  fontWeight: 600,
  color: '#B45309',
  background: '#FEF3C7',
  borderRadius: 999,
  whiteSpace: 'nowrap',
}

const taxPillBaseStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '1px 8px',
  fontSize: 10.5,
  fontWeight: 600,
  borderRadius: 999,
  whiteSpace: 'nowrap',
}

export const customTaxPillStyle: React.CSSProperties = {
  ...taxPillBaseStyle,
  color: '#1D4ED8',
  background: '#DBEAFE',
}

export const defaultTaxPillStyle: React.CSSProperties = {
  ...taxPillBaseStyle,
  color: '#64748B',
  background: '#F1F5F9',
}

/** Effective zone tax + Custom/Default pill, shared with the Overview card. */
export const ZoneTaxCell: React.FC<{
  zone: MailOrderShippingZone
  defaultTax: string
}> = ({ zone, defaultTax }) => {
  const { value, inherited } = getEffectiveZoneTax(zone, defaultTax)
  return (
    <Tooltip
      text={
        inherited
          ? `Inherited from the location shipping tax (${formatTaxPercent(defaultTax)})`
          : 'Custom rate for this zone — overrides the location shipping tax'
      }
      width={220}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: inherited ? '#7F8C8D' : '#2C3E50' }}>
          {formatTaxPercent(value)}
        </span>
        <span style={inherited ? defaultTaxPillStyle : customTaxPillStyle}>
          {inherited ? 'Default' : 'Custom'}
        </span>
      </span>
    </Tooltip>
  )
}

const excludedGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 100px 40px',
  alignItems: 'center',
  padding: '8px 16px',
  borderBottom: '1px solid #F1F5F9',
}

export const MapPinIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 18,
  color = '#2563EB',
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
)

const ChevronIcon: React.FC<{ up: boolean }> = ({ up }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points={up ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
  </svg>
)

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
)

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
  </svg>
)

function fmtMoney(value: string | number): string {
  const num = typeof value === 'number' ? value : Number.parseFloat(value)
  return `$${(Number.isFinite(num) ? num : 0).toFixed(2)}`
}

interface Props {
  zone: MailOrderShippingZone
  expanded: boolean
  saving: boolean
  isPendingDelete: boolean
  lookups: CategoryLookups
  categoriesLoading: boolean
  defaultShippingTax: string
  onToggleExpand: () => void
  onEdit: () => void
  onDuplicate: () => void
  onDelete: () => void
  onRemoveExclusion: (categoryId: number) => void
}

export const ShippingZoneRow: React.FC<Props> = ({
  zone,
  expanded,
  saving,
  isPendingDelete,
  lookups,
  categoriesLoading,
  defaultShippingTax,
  onToggleExpand,
  onEdit,
  onDuplicate,
  onDelete,
  onRemoveExclusion,
}) => {
  const excluded = zone.excluded_categories ?? []
  const stateName = getStateName(zone.country, zone.state)
  const region = `${stateName}, ${getCountryName(zone.country)}`
  const { nameById, pathById, countById, imageById } = lookups

  return (
    <>
      <div style={zoneTableRowStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <MapPinIcon size={14} color="#7F8C8D" />
          <span style={{ fontWeight: 500 }}>{zone.name || stateName}</span>
        </div>
        <div style={{ fontSize: 12, color: '#5A6B7B' }}>{region}</div>
        <div>{fmtMoney(zone.flat_rate)}</div>
        <div>{fmtMoney(zone.minimum_order)}</div>
        <div>{fmtMoney(zone.free_shipping_over)}</div>
        <div>
          <ZoneTaxCell zone={zone} defaultTax={defaultShippingTax} />
        </div>
        <div>
          {excluded.length > 0 ? (
            <span style={warningBadgeStyle}>{excluded.length} excluded</span>
          ) : (
            <span style={{ fontSize: 12, color: '#94A3B8' }}>None</span>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
          <button
            type="button"
            onClick={onToggleExpand}
            style={iconBtnStyle}
            aria-label={expanded ? 'Hide excluded categories' : 'Show excluded categories'}
            aria-expanded={expanded}
          >
            <ChevronIcon up={expanded} />
          </button>
          <button
            type="button"
            onClick={onEdit}
            disabled={saving}
            style={{ ...iconBtnStyle, opacity: saving ? 0.4 : 1 }}
            aria-label="Edit zone"
          >
            <EditIcon />
          </button>
          <button
            type="button"
            onClick={onDuplicate}
            disabled={saving}
            style={{ ...iconBtnStyle, opacity: saving ? 0.4 : 1 }}
            aria-label="Duplicate zone"
            title="Duplicate zone"
          >
            <CopyIcon />
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={saving}
            style={{
              ...iconBtnStyle,
              opacity: saving ? 0.4 : 1,
              color: isPendingDelete ? '#DC2626' : '#7F8C8D',
            }}
            aria-label="Delete zone"
          >
            <TrashIcon />
          </button>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '16px 20px', background: '#F8FAFC', borderBottom: '1px solid #F1F5F9' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div>
              <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                Excluded Categories
              </p>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: '#7F8C8D' }}>
                Products in these categories (and their sub-categories) won't be available
                for mail order to {region}
              </p>
            </div>
            <button
              type="button"
              onClick={onEdit}
              disabled={saving}
              style={{
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 600,
                color: '#374151',
                background: '#fff',
                border: '1px solid #E5E7EB',
                borderRadius: 8,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.55 : 1,
                whiteSpace: 'nowrap',
              }}
            >
              + Add Categories
            </button>
          </div>

          {excluded.length > 0 ? (
            <div style={{ background: '#fff', border: '1px solid #E8ECF0', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ ...excludedGridStyle, fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                <span>Category</span>
                <span style={{ textAlign: 'right' }}>Products</span>
                <span />
              </div>
              {excluded.map((id) => {
                const name = nameById.get(id) ?? `#${id}`
                return (
                  <div key={id} style={{ ...excludedGridStyle, fontSize: 13 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <CategoryAvatar name={name} image={imageById.get(id)} />
                      <span style={{ fontWeight: 500 }}>{pathById.get(id) ?? name}</span>
                    </div>
                    <span style={{ textAlign: 'right', fontSize: 12, color: '#5A6B7B' }}>
                      {categoriesLoading && !nameById.has(id) ? '—' : (countById.get(id) ?? 0)}
                    </span>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        onClick={() => onRemoveExclusion(id)}
                        disabled={saving}
                        style={{ ...iconBtnStyle, opacity: saving ? 0.4 : 1 }}
                        aria-label={`Remove ${name}`}
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: 16,
                background: '#fff',
                border: '2px dashed #E8ECF0',
                borderRadius: 8,
                fontSize: 13,
                color: '#7F8C8D',
              }}
            >
              <span style={{ color: '#16A34A', fontWeight: 700 }}>✓</span>
              No excluded categories — all products can be shipped to {region}
            </div>
          )}
        </div>
      )}
    </>
  )
}
